/**
 * 65.12 — the field kit's small things, at the person's scale (a person is 1.8), in the E2 kit's style (§10.2: one
 * metal, one flash, panels not greebles): the HE grenade, the smoke grenade and its spent canister on the ground,
 * and the bandage — the pouch carried on the belt, the roll in the hand, the dressing on a limb.
 *
 * Models only (the owner, Oct 10: "getting the models together isn't a bad idea"). The grenades wait for 65.15 (the
 * throw, the fuse, the smoke's cloud); the person model carries them in chest pouches when its kit says so and
 * shows a throw's hands (`throwAt`). The bandage's loop is 65.7's: the roll shows in the strong hand while it runs
 * and the dressing grows on the support forearm, then stays (`dressed`). Both hands by construction: a right-handed
 * person is the model mirrored (67.21).
 *
 * Origins: a grenade at its body's centre (the palm's hold point); the canister at its middle, lying along +z; the
 * roll at its axle (along x); the dressing round a limb's axis (along y), sized to sit over the forearm.
 */
import * as THREE from 'three';
import { markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';

export type GrenadeKind = 'he' | 'smoke';

export interface GrenadeModel {
  group: THREE.Group;
  /** the lever along the body, and the pin's ring (a thrown grenade has lost both) */
  spoon: THREE.Object3D;
  ring: THREE.Object3D;
}

/** The kit's numbers, in person units. */
export const FIELD_KIT = {
  he: { radius: 0.042, stretch: 1.2 },
  smoke: { radius: 0.034, length: 0.13 },
  roll: { radius: 0.036, width: 0.07 },
  /** the dressing's band over the forearm (its box is 0.14 × 0.15), and its width along the arm */
  dressing: { across: 0.162, deep: 0.172, width: 0.1 },
};
/** The smoke's own colours: the canister's pale body and the cloud it makes (the grenade's band is the team's flash). */
export const SMOKE_LOOK = { body: '#c9c6bb', cloud: '#d8d6d0' };
const OLIVE = '#4f5640', GAUZE = '#ece8dc', GAUZE_EDGE = '#d6cfbd';

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}
function cylinder(parent: THREE.Object3D, r: number, h: number, color: string, x: number, y: number, z: number, sides = 8): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, sides), mat(color));
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}

/** The fuse head, the spoon down the body's side and the pin's ring: both grenades share them. */
function fuse(g: THREE.Group, top: number, side: number, p: UnitPalette): { spoon: THREE.Object3D; ring: THREE.Object3D } {
  box(g, 0.03, 0.03, 0.03, p.metal, 0, top + 0.012, 0);                       // the fuse head
  const spoon = box(g, 0.016, top + 0.02, 0.008, p.metal, 0, top / 2 - 0.004, side + 0.006);   // the lever, flat against the body
  spoon.rotation.x = -0.08;
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.016, 0.003, 4, 10), mat(p.metal));
  ring.position.set(0.026, top + 0.014, 0); ring.rotation.y = Math.PI / 2;
  g.add(ring);
  return { spoon, ring };
}

/**
 * The HE grenade: a dark-olive egg (faceted, the style's low count) with the team's flash as a band round its
 * waist, the fuse head on top, the spoon down its side, the pin's ring out to the right. The smoke grenade: a pale
 * canister — longer, straight-sided, so the two read apart in the hand — with the team's band near the top and the
 * same fuse.
 */
export function buildGrenade(kind: GrenadeKind, p: UnitPalette): GrenadeModel {
  const g = new THREE.Group();
  if (kind === 'he') {
    const { radius: r, stretch } = FIELD_KIT.he;
    const body = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 1), mat(OLIVE));
    body.scale.set(1, stretch, 1);
    g.add(body);
    cylinder(g, r * 1.02, 0.012, p.flash, 0, 0, 0, 10);                        // the band: the team's, and live
    const parts = fuse(g, r * stretch, r * 0.92, p);
    return { group: g, ...parts };
  }
  const { radius: r, length: h } = FIELD_KIT.smoke;
  cylinder(g, r, h, SMOKE_LOOK.body, 0, 0, 0, 10);
  cylinder(g, r * 1.03, 0.018, p.flash, 0, h * 0.28, 0, 10);                 // the band near the top
  cylinder(g, r * 1.03, 0.008, '#8a877e', 0, -h * 0.47, 0, 10);              // the base's rim
  const parts = fuse(g, h / 2, r, p);
  return { group: g, ...parts };
}

/**
 * The spent smoke canister on the ground: the grenade lying on its side along +z, its lever and pin gone, the vent
 * holes round its top end dark (the cloud comes out of them — the cloud itself is 65.15's effect).
 */
export function buildSmokeCanister(p: UnitPalette): THREE.Group {
  const g = new THREE.Group();
  const lying = new THREE.Group();
  lying.rotation.x = Math.PI / 2;                                             // its top toward +z
  lying.position.y = FIELD_KIT.smoke.radius;                                  // resting on the ground
  const { radius: r, length: h } = FIELD_KIT.smoke;
  cylinder(lying, r, h, SMOKE_LOOK.body, 0, 0, 0, 10);
  cylinder(lying, r * 1.03, 0.018, p.flash, 0, h * 0.28, 0, 10);
  box(lying, 0.03, 0.022, 0.03, p.metal, 0, h / 2 + 0.008, 0);               // the fuse's stub
  for (let i = 0; i < 4; i++) {                                               // the vents, scorched
    const a = (i / 4) * Math.PI * 2;
    box(lying, 0.012, 0.016, 0.012, '#2a2622', Math.cos(a) * r * 0.9, h * 0.42, Math.sin(a) * r * 0.9);
  }
  g.add(lying);
  return g;
}

/**
 * The bandage's pouch on the belt: a squat dark-olive pouch with a light flap and a white plus on it (the
 * generic first-aid mark — not a red cross, which is a protected emblem).
 */
export function buildMedPouch(p: UnitPalette): THREE.Group {
  const g = new THREE.Group();
  box(g, 0.13, 0.13, 0.07, OLIVE, 0, 0, 0);
  box(g, 0.135, 0.045, 0.075, p.dark, 0, 0.05, 0.001);                        // the flap
  box(g, 0.06, 0.016, 0.004, GAUZE, 0, -0.012, 0.037);                       // the plus
  box(g, 0.016, 0.06, 0.004, GAUZE, 0, -0.012, 0.037);
  return g;
}

/** The roll in the hand: a white roll on its axle (along x), a tail of gauze hanging from it. `left` 0…1 shrinks it as it unwinds. */
export function buildBandageRoll(): THREE.Group {
  const g = new THREE.Group();
  const roll = new THREE.Mesh(new THREE.CylinderGeometry(FIELD_KIT.roll.radius, FIELD_KIT.roll.radius, FIELD_KIT.roll.width, 10), mat(GAUZE));
  roll.rotation.z = Math.PI / 2;
  g.add(roll);
  box(g, FIELD_KIT.roll.width * 0.9, 0.06, 0.004, GAUZE_EDGE, 0, -0.045, FIELD_KIT.roll.radius);   // a tail of gauze
  return g;
}
/** The roll unwinding: `left` 1 full … 0.45 most of it on the arm (it thins across its axle, which runs along x; the group scales, so a baked roll works too). */
export function setRollLeft(roll: THREE.Group, left: number): void {
  const k = Math.max(0.45, Math.min(1, left));
  roll.scale.set(1, k, k);
}

/**
 * The dressing: a band of gauze round a limb, sitting just over the forearm's box, with a darker edge at each side
 * and a pad over the wound. Its axis along y (the limb's), centred on the wound.
 */
export function buildDressing(): THREE.Group {
  const g = new THREE.Group();
  const { across: w, deep: d, width: h } = FIELD_KIT.dressing;
  box(g, w, h, d, GAUZE, 0, 0, 0);
  for (const y of [-h / 2, h / 2]) box(g, w + 0.004, 0.012, d + 0.004, GAUZE_EDGE, 0, y, 0);
  box(g, w * 0.5, h * 0.7, 0.012, GAUZE_EDGE, 0, 0, d / 2 + 0.004);           // the pad over the wound, on the front
  return g;
}
